import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  ArrowRight, Eye, EyeOff, ChevronDown, Play, VolumeX, Volume2,
  Shield, Users, Calendar, MessageSquare, Sparkles, Zap, Lock, CheckCircle2
} from "lucide-react";

const VIDEO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/kinput_showreel.mp4`;

const ACCENT = "#00e5ff";

export default function LandingPage() {
  const [mode, setMode] = useState("landing");
  const { login, verify2FA, register, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [muted, setMuted] = useState(true);
  const [memberCount, setMemberCount] = useState(null);
  // 2FA step-2 state
  const [twoFaStep, setTwoFaStep] = useState(false);
  const [twoFaToken, setTwoFaToken] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  // Forgot-password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const aboutRef = useRef(null);
  const revealRefs = useRef([]);

  // Scroll reveal via IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  // Fetch live community stats (public endpoint)
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && typeof d.members === "number") setMemberCount(d.members); })
      .catch(() => {});
  }, []);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regFirstName, setRegFirstName] = useState("");
  const [regLastName, setRegLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");

  useEffect(() => {
    // Respect ?redirect=/events/festival/xxx after successful approval login
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");
    if (user && user.status === "approved") {
      navigate(redirect && redirect.startsWith("/") ? redirect : "/board");
    } else if (user && user.status === "pending") {
      navigate("/pending");
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault(); setError(""); setIsLoading(true);
    const r = await login(loginEmail, loginPassword);
    setIsLoading(false);
    if (!r.success) { setError(r.error); return; }
    if (r.requires_2fa) {
      setTwoFaToken(r.temp_token);
      setTwoFaStep(true);
      setTwoFaCode("");
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault(); setError(""); setIsLoading(true);
    const r = await verify2FA(twoFaToken, twoFaCode);
    setIsLoading(false);
    if (!r.success) { setError(r.error); return; }
    // Reset step-2 state — user state in context triggers navigation via effect
    setTwoFaStep(false); setTwoFaToken(""); setTwoFaCode("");
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setError(""); setIsLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch {
      setError("Anfrage fehlgeschlagen. Bitte versuche es später erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError("");
    if (regPassword.length < 8) { setError("Passwort muss mindestens 8 Zeichen lang sein"); return; }
    if (regPassword !== regConfirm) { setError("Passwörter stimmen nicht überein"); return; }
    if (!accepted) { setError("Bitte akzeptiere die Nutzungsbedingungen"); return; }
    setIsLoading(true);
    const r = await register(regFirstName, regLastName, regEmail, regPassword);
    setIsLoading(false); if (!r.success) setError(r.error);
  };

  const toggleVideo = () => {
    const v = document.getElementById("intro-video");
    if (!v) return;
    if (videoPlaying) {
      v.pause(); setVideoPlaying(false);
    } else {
      // Manuelles Play: Ton an
      v.muted = false; setMuted(false);
      v.play(); setVideoPlaying(true);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 selection:text-white">
      {/* ============================================================ */}
      {/* ===== HERO SECTION ========================================= */}
      {/* ============================================================ */}
      <section
        className="relative w-full overflow-hidden bg-black"
        style={{ minHeight: "100svh", height: "100dvh" }}
      >
        {/* 4K Brand Backdrop Video (mobile-split + poster fallback) */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_poster.jpg`}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <video
            autoPlay muted loop playsInline preload="metadata"
            poster={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_poster.jpg`}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-backdrop-video"
          >
            <source src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg_mobile.mp4`} type="video/mp4" media="(max-width: 768px)" />
            <source src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/events/hero_bg.mp4`} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/50" />
        </div>

        {/* Ambient Glow Blobs — premium depth */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
          <div className="hero-blob hero-blob-3" />
        </div>

        {/* Subtle grid texture */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.15] hero-grid" />

        {/* Noise overlay for film-grain premium feel */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.035] hero-noise mix-blend-overlay" />

        {/* Gradient overlay to ensure text readability — bottom vignette */}
        <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-[25%] bg-gradient-to-b from-black/80 via-black/30 to-transparent pointer-events-none" />

        {/* ===== Top Navigation ===== */}
        <nav className="absolute top-0 inset-x-0 z-30 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex items-start justify-between">
          <div className="flex flex-col items-start gap-1.5" data-testid="landing-logo">
            <span className="font-outfit font-bold text-base sm:text-lg tracking-tight text-white leading-none">
              <span className="text-[#00e5ff]">[</span><span className="text-[#00e5ff]">KI</span><span className="text-[#00e5ff]">]</span>NPUT
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.26em] text-white/40 font-outfit">powered by</span>
              <img
                src="https://customer-assets.emergentagent.com/job_design-skills-hub-3/artifacts/j8c4vziw_teamwerk_Logo_RZ_simple.svg"
                alt="teamWERK Erolzheim"
                className="h-4 sm:h-5 w-auto opacity-85 hover:opacity-100 transition-opacity"
                style={{ filter: "invert(1) brightness(1.1)" }}
                data-testid="landing-teamwerk-logo"
              />
            </div>
            <span className="hidden md:inline-flex items-center gap-2 mt-1 px-2.5 py-1 border border-[#00e5ff]/20 bg-[#00e5ff]/5 backdrop-blur-md">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00e5ff] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#00e5ff]"></span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-outfit">Community aktiv</span>
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-5 text-sm">
            <a href="/legal" className="hidden sm:inline text-white/60 hover:text-white transition-colors tracking-wide">Rechtliches</a>
            <button
              onClick={() => setMode("login")}
              className="text-white/80 hover:text-white transition-colors tracking-wide font-outfit font-medium"
              data-testid="nav-login-button"
            >
              Anmelden
            </button>
            <button
              onClick={() => setMode("register")}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 border border-white/20 hover:border-[#00e5ff] hover:text-[#00e5ff] text-white/90 transition-all duration-300 font-outfit text-sm tracking-wide"
            >
              Zugang
              <ArrowRight size={14} />
            </button>
          </div>
        </nav>

        {/* ===== Hero Content — glass card bottom-center ===== */}
        <div className="absolute inset-x-0 bottom-6 sm:bottom-10 z-20 px-4 flex flex-col items-center">
          <div className="w-full max-w-2xl hero-reveal">
            {/* Gradient border wrapper */}
            <div className="relative group">
              <div
                className="absolute -inset-px opacity-60 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}55 0%, transparent 40%, transparent 60%, ${ACCENT}33 100%)`,
                }}
              />
              <div className="relative backdrop-blur-2xl bg-black/55 border border-white/[0.08] p-5 sm:p-8">
                {/* Eyebrow */}
                <div className="flex items-center justify-center gap-2 mb-4 sm:mb-5">
                  <Sparkles size={13} className="text-[#00e5ff]" />
                  <span className="text-xs sm:text-sm uppercase tracking-[0.28em] text-[#00e5ff] font-outfit font-semibold">
                    KI-Hub · Illertal
                  </span>
                  <Sparkles size={13} className="text-[#00e5ff]" />
                </div>

                {/* Heading */}
                <h1
                  className="font-outfit font-black text-center text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[0.95] text-white mb-4"
                  data-testid="hero-heading"
                >
                  <span className="text-[#00e5ff]">[</span>
                  <span className="text-[#00e5ff]">KI</span>
                  <span className="text-[#00e5ff]">]</span>
                  NPUT
                </h1>

                {/* Subtitle */}
                <p className="text-center text-xl sm:text-2xl text-white/90 font-outfit font-semibold tracking-wide mb-5" data-testid="hero-subtitle">
                  Der regionale KI-Hub für Unternehmer
                </p>

                {/* What is this platform — short pillars */}
                <ul
                  className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mb-7"
                  data-testid="hero-pillars"
                >
                  {["Events", "Austauschen", "Diskutieren", "KI lernen", "KI verstehen"].map((p) => (
                    <li
                      key={p}
                      className="px-3.5 py-1.5 border border-[#00e5ff]/30 bg-[#00e5ff]/[0.04] text-[#00e5ff] text-sm sm:text-base font-outfit tracking-wide"
                    >
                      {p}
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <Button
                    onClick={() => setMode("register")}
                    data-testid="hero-register-btn"
                    className="group/btn relative overflow-hidden bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none px-6 sm:px-8 h-13 sm:h-14 font-outfit font-bold text-base sm:text-lg tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)]"
                  >
                    <span className="relative z-10 flex items-center">
                      Zugang beantragen
                      <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover/btn:translate-x-0.5" />
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setMode("login")}
                    data-testid="hero-login-btn"
                    className="text-white/80 hover:text-white hover:bg-white/5 rounded-none px-5 sm:px-6 h-13 sm:h-14 font-outfit text-base sm:text-lg tracking-wide border border-white/15 hover:border-white/30 transition-all duration-300"
                  >
                    Anmelden
                  </Button>
                </div>

                {/* Trust indicators */}
                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 sm:mt-7 pt-5 sm:pt-6 border-t border-white/[0.06]">
                  <div className="flex items-center gap-1.5 text-sm sm:text-base text-white/55 font-outfit">
                    <Shield size={14} className="text-[#00e5ff]/80" />
                    <span>Nur geprüfte Mitglieder</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm sm:text-base text-white/55 font-outfit">
                    <Lock size={14} className="text-[#00e5ff]/80" />
                    <span>Admin-Freischaltung</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm sm:text-base text-white/55 font-outfit">
                    <Zap size={14} className="text-[#00e5ff]/80" />
                    <span>Regional · Illertal</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scroll indicator */}
            <button
              onClick={() => aboutRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="mx-auto mt-4 sm:mt-5 text-white/40 hover:text-[#00e5ff] transition-colors flex flex-col items-center gap-1 group/scroll"
              aria-label="Weiterscrollen"
            >
              <span className="text-xs sm:text-sm uppercase tracking-[0.3em] font-outfit font-medium">Entdecken</span>
              <ChevronDown size={22} className="animate-bounce group-hover/scroll:text-[#00e5ff]" />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* ===== STATS STRIP ========================================== */}
      {/* ============================================================ */}
      <section className="relative border-y border-white/[0.06] bg-gradient-to-b from-black to-[#050505] py-8 sm:py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.04]" ref={addReveal}>
          {[
            { num: "100%", label: "Regional", sub: "aus dem Illertal" },
            {
              num: memberCount === null ? "…" : String(memberCount),
              label: "Mitglieder",
              sub: memberCount === 1 ? "aktiv dabei" : "aktiv dabei",
            },
            { num: "KMU", label: "Fokus", sub: "kleine & mittlere" },
            { num: "KI", label: "Praxis", sub: "statt Theorie" },
          ].map((s, i) => (
            <div
              key={s.label}
              className="bg-black px-4 sm:px-6 py-5 sm:py-6 transition-colors duration-300 hover:bg-[#00e5ff]/[0.025] stat-item"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="font-outfit font-black text-3xl sm:text-4xl md:text-5xl text-white tracking-tighter">
                <span className="text-[#00e5ff]">{s.num}</span>
              </div>
              <div className="mt-2 text-sm sm:text-base uppercase tracking-[0.24em] text-white/75 font-outfit font-semibold">{s.label}</div>
              <div className="text-sm sm:text-base text-white/50 mt-1">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* ===== ABOUT / MANIFESTO ==================================== */}
      {/* ============================================================ */}
      <section ref={aboutRef} className="relative py-20 sm:py-28 px-4 sm:px-6 bg-[#050505] overflow-hidden">
        {/* decorative side accent */}
        <div className="absolute left-0 top-32 w-px h-32 bg-gradient-to-b from-transparent via-[#00e5ff]/30 to-transparent" />
        <div className="max-w-5xl mx-auto">
          <div className="reveal-up" ref={addReveal}>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#00e5ff]" />
              <p className="font-outfit text-sm sm:text-base uppercase tracking-[0.32em] text-[#00e5ff] font-semibold">
                Über [KI]NPUT
              </p>
            </div>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl md:text-6xl text-white mb-6 leading-[1.05] tracking-tight max-w-3xl">
              Wir sind keine KI-Experten.<br />
              <span className="text-white/40">Wir sind </span>
              <span className="relative inline-block">
                Unternehmer.
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#00e5ff]/60" />
              </span>
            </h2>
            <p className="text-white/75 leading-relaxed max-w-2xl text-lg sm:text-xl mb-6">
              Seit Monaten probieren wir aus, scheitern manchmal, staunen öfter. [KI]NPUT ist ein
              <span className="text-white"> Format und eine Informationsplattform</span> aus dem
              {" "}<span className="text-[#00e5ff] font-semibold">Illertal</span>
              {" "}— für die Region <span className="text-white font-semibold">Oberschwaben &amp; Allgäu</span>.
              Hier teilen wir diese Erfahrungen ohne Pitch, ohne Hype. Nur echter Austausch
              unter Unternehmern, die KI verstehen und sinnvoll einsetzen wollen.
            </p>
            <p className="text-white/70 leading-relaxed max-w-2xl text-lg sm:text-xl mb-14 sm:mb-16" data-testid="about-discuss-text">
              Hier werden <span className="text-white font-semibold">unterschiedliche Meinungen diskutiert</span>.
              Wir wollen uns mit KI auseinandersetzen — welche <span className="text-[#00e5ff]">positiven Aspekte</span>,
              aber auch welche <span className="text-[#00e5ff]">negativen Auswirkungen</span> sie haben kann.
            </p>
          </div>

          {/* Feature grid — bento style with glass accents */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 reveal-up" ref={addReveal}>
            {[
              { icon: Calendar, title: "Themenabende", desc: "Vorträge & Diskussion", tag: "01" },
              { icon: MessageSquare, title: "Deep Dives", desc: "Coding & Workshops", tag: "02" },
              { icon: Users, title: "Community", desc: "Nur geprüfte Mitglieder", tag: "03" },
              { icon: Shield, title: "Kritisch", desc: "Nicht nur Hype", tag: "04" },
            ].map((f, i) => (
              <div
                key={f.title}
                className="group relative p-5 sm:p-6 border border-white/[0.07] bg-gradient-to-br from-white/[0.02] to-transparent hover:from-[#00e5ff]/[0.04] hover:border-[#00e5ff]/20 transition-all duration-500 overflow-hidden"
              >
                {/* Hover gradient */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 80% 0%, ${ACCENT}12 0%, transparent 50%)` }}
                />
                <div className="relative">
                  <div className="flex items-start justify-between mb-4 sm:mb-5">
                    <f.icon size={22} className="text-white/40 group-hover:text-[#00e5ff] transition-colors duration-300" strokeWidth={1.5} />
                    <span className="text-[10px] font-outfit tracking-widest text-white/15 group-hover:text-[#00e5ff]/40 transition-colors">{f.tag}</span>
                  </div>
                  <p className="text-base sm:text-lg font-semibold text-white mb-1 font-outfit tracking-tight">{f.title}</p>
                  <p className="text-sm sm:text-base text-white/60 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* ===== VIDEO / EINBLICK ===================================== */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 bg-black border-t border-white/[0.06] overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-[#00e5ff]/[0.03] blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center reveal-up" ref={addReveal}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#00e5ff]" />
            <p className="font-outfit text-sm sm:text-base uppercase tracking-[0.32em] text-[#00e5ff] font-semibold">Einblick</p>
            <span className="w-8 h-px bg-[#00e5ff]" />
          </div>
          <h3 className="font-outfit font-bold text-3xl sm:text-4xl md:text-5xl text-white mb-10 sm:mb-12 tracking-tight">
            Seht selbst, was uns antreibt.
          </h3>

          <div className="relative group">
            {/* Corner brackets for premium feel */}
            <span className="absolute -top-1 -left-1 w-5 h-5 border-t border-l border-[#00e5ff]/40 group-hover:border-[#00e5ff] transition-colors" />
            <span className="absolute -top-1 -right-1 w-5 h-5 border-t border-r border-[#00e5ff]/40 group-hover:border-[#00e5ff] transition-colors" />
            <span className="absolute -bottom-1 -left-1 w-5 h-5 border-b border-l border-[#00e5ff]/40 group-hover:border-[#00e5ff] transition-colors" />
            <span className="absolute -bottom-1 -right-1 w-5 h-5 border-b border-r border-[#00e5ff]/40 group-hover:border-[#00e5ff] transition-colors" />

            <div className="relative aspect-video bg-[#0a0a0a] border border-white/[0.08] overflow-hidden">
              <video
                id="intro-video"
                src={VIDEO_URL}
                className="w-full h-full object-cover cursor-pointer"
                muted
                loop
                playsInline
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
                onClick={() => { if (videoPlaying) toggleVideo(); }}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  if (v.duration && !isNaN(v.duration)) {
                    setVideoProgress((v.currentTime / v.duration) * 100);
                  }
                }}
              />
              {!videoPlaying && (
                <button
                  className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-black/30 via-black/50 to-black/70 cursor-pointer group/play"
                  onClick={toggleVideo}
                  aria-label="Video abspielen"
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 border border-white/30 flex items-center justify-center transition-all duration-300 group-hover/play:border-[#00e5ff] group-hover/play:bg-[#00e5ff]/10 group-hover/play:scale-105">
                    <Play size={24} className="text-white ml-1 group-hover/play:text-[#00e5ff] transition-colors" fill="white" />
                  </div>
                </button>
              )}
              {videoPlaying && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const v = document.getElementById("intro-video");
                    if (v) { v.muted = !v.muted; setMuted(!muted); }
                  }}
                  className="absolute bottom-4 right-3 p-2 bg-black/70 backdrop-blur-sm border border-white/10 text-white/60 hover:text-[#00e5ff] hover:border-[#00e5ff]/40 transition-colors"
                  aria-label={muted ? "Ton an" : "Ton aus"}
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
              )}
              {/* Cyan progress bar — nur visuell, nicht klickbar */}
              <div className="absolute left-0 right-0 bottom-0 h-[3px] bg-white/[0.06] pointer-events-none" data-testid="landing-video-progress">
                <div
                  className="h-full bg-[#00e5ff] transition-[width] duration-100 ease-linear shadow-[0_0_10px_rgba(0,229,255,0.7)]"
                  style={{ width: `${videoProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* ===== FINAL CTA ============================================ */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-black via-[#040414] to-black border-t border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-[0.08] pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#00e5ff]/[0.04] blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto text-center reveal-up" ref={addReveal}>
          <Sparkles size={28} className="text-[#00e5ff]/70 mx-auto mb-6" strokeWidth={1.5} />
          <h3 className="font-outfit font-black text-4xl sm:text-5xl md:text-6xl text-white tracking-tighter leading-[1.05] mb-6">
            Bereit für echten<br />
            <span className="text-[#00e5ff]">KI-Austausch?</span>
          </h3>
          <p className="text-white/70 text-lg sm:text-xl max-w-xl mx-auto mb-10 leading-relaxed">
            Beantrage jetzt deinen Zugang. Ein Admin prüft deine Anfrage und schaltet dich anschließend frei.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={() => setMode("register")}
              data-testid="cta-register-btn"
              className="group/cta relative overflow-hidden bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none px-10 h-14 font-outfit font-bold text-base sm:text-lg tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_40px_-4px_rgba(0,229,255,0.7)]"
            >
              <span className="relative z-10 flex items-center">
                Zugang beantragen
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover/cta:translate-x-1" />
              </span>
            </Button>
            <button
              onClick={() => setMode("login")}
              className="text-white/75 hover:text-white text-base sm:text-lg font-outfit tracking-wide h-14 px-5 border border-transparent hover:border-white/20 transition-all"
            >
              Bereits Mitglied? Anmelden →
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm sm:text-base text-white/50 font-outfit">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#00e5ff]/70" /> Kostenlos</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#00e5ff]/70" /> Admin-geprüft</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#00e5ff]/70" /> DSGVO-konform</span>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* ===== AUTH MODAL =========================================== */}
      {/* ============================================================ */}
      {(mode === "login" || mode === "register" || mode === "forgot") && (
        <div
          className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[6px] flex items-center justify-center p-4 auth-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setMode("landing"); }}
        >
          <div className="w-full max-w-md auth-modal-card">
            {/* Gradient border wrapper */}
            <div className="relative">
              <div
                className="absolute -inset-px pointer-events-none"
                style={{ background: `linear-gradient(135deg, ${ACCENT}40 0%, transparent 50%, ${ACCENT}20 100%)` }}
              />
              <div className="relative border border-white/10 bg-[#0a0a0a] p-6 sm:p-8">
                {mode === "login" && !twoFaStep && (
                  <div className="space-y-5" data-testid="login-form-container">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-px bg-[#00e5ff]/60" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit">Anmelden</span>
                      </div>
                      <h2 className="font-outfit font-bold text-2xl text-white tracking-tight">Willkommen zurück</h2>
                      <p className="text-xs text-white/40 mt-1">Dein KI-Hub im Illertal</p>
                    </div>
                    {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2.5" data-testid="auth-error">{error}</div>}
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">E-Mail</Label>
                        <Input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                          data-testid="login-email-input" className="mt-1.5 bg-black border-white/10 text-white rounded-none h-11 focus:border-[#00e5ff]/60 focus:ring-0 transition-colors" placeholder="deine@email.de" />
                      </div>
                      <div>
                        <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">Passwort</Label>
                        <div className="relative mt-1.5">
                          <Input type={showPassword ? "text" : "password"} required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                            data-testid="login-password-input" className="bg-black border-white/10 text-white rounded-none h-11 pr-10 focus:border-[#00e5ff]/60 focus:ring-0" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-[#00e5ff]/80 transition-colors">
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" disabled={isLoading} data-testid="login-submit-button"
                        className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-sm tracking-wide transition-all">
                        {isLoading ? "..." : "Anmelden"}
                      </Button>
                    </form>
                    <button
                      onClick={() => { setMode("forgot"); setError(""); }}
                      data-testid="forgot-password-link"
                      className="block w-full text-center text-xs text-white/40 hover:text-[#00e5ff]/80 transition-colors -mt-2"
                    >
                      Passwort vergessen?
                    </button>
                    <button onClick={() => { setMode("register"); setError(""); }} data-testid="switch-to-register"
                      className="w-full text-center text-sm text-white/40 hover:text-[#00e5ff]/80 transition-colors">
                      Noch kein Konto? <span className="underline underline-offset-4">Zugang beantragen</span>
                    </button>
                  </div>
                )}

                {mode === "forgot" && (
                  <div className="space-y-5" data-testid="forgot-container">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-px bg-[#00e5ff]/60" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit">Passwort-Reset</span>
                      </div>
                      <h2 className="font-outfit font-bold text-2xl text-white tracking-tight">Passwort vergessen</h2>
                      <p className="text-xs text-white/50 mt-1 leading-relaxed">
                        Gib deine E-Mail ein. Wir senden dir einen Reset-Link (gültig 60 Minuten).
                      </p>
                    </div>
                    {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2.5">{error}</div>}
                    {forgotSent && (
                      <div className="text-[#00e5ff] text-sm bg-[#00e5ff]/5 border border-[#00e5ff]/30 px-3 py-2.5" data-testid="forgot-sent">
                        Wenn die E-Mail bei uns registriert ist, hast du eine Nachricht mit Reset-Link erhalten. Prüfe auch den Spam-Ordner.
                      </div>
                    )}
                    {!forgotSent && (
                      <form onSubmit={handleForgot} className="space-y-4">
                        <div>
                          <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">E-Mail</Label>
                          <Input
                            type="email"
                            required
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            data-testid="forgot-email-input"
                            className="mt-1.5 bg-black border-white/10 text-white rounded-none h-11 focus:border-[#00e5ff]/60 focus:ring-0"
                            placeholder="deine@email.de"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={isLoading || !forgotEmail}
                          data-testid="forgot-submit"
                          className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none font-outfit font-semibold h-11 text-sm"
                        >
                          {isLoading ? "…" : "Reset-Link anfordern"}
                        </Button>
                      </form>
                    )}
                    <button
                      onClick={() => { setMode("login"); setError(""); setForgotSent(false); }}
                      data-testid="forgot-back"
                      className="w-full text-center text-sm text-white/40 hover:text-[#00e5ff]/80 transition-colors"
                    >
                      ← Zurück zur Anmeldung
                    </button>
                  </div>
                )}

                {mode === "login" && twoFaStep && (
                  <div className="space-y-5" data-testid="login-2fa-container">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-px bg-[#00e5ff]/60" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit">Zwei-Faktor</span>
                      </div>
                      <h2 className="font-outfit font-bold text-2xl text-white tracking-tight">Sicherheits-Code</h2>
                      <p className="text-xs text-white/50 mt-1 leading-relaxed">
                        Öffne deine Authenticator-App (Google Authenticator, Authy, 1Password ...) und gib den 6-stelligen Code ein. Alternativ kannst du einen Backup-Code verwenden.
                      </p>
                    </div>
                    {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2.5" data-testid="auth-error">{error}</div>}
                    <form onSubmit={handle2FA} className="space-y-4">
                      <div>
                        <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">Code</Label>
                        <Input
                          autoFocus
                          required
                          value={twoFaCode}
                          onChange={(e) => setTwoFaCode(e.target.value.toUpperCase().slice(0, 10))}
                          data-testid="login-2fa-input"
                          placeholder="123456 oder XXXX-XXXX"
                          className="mt-1.5 bg-black border-white/10 text-white rounded-none h-12 text-center tracking-[0.3em] font-mono text-base focus:border-[#00e5ff]/60 focus:ring-0"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isLoading || twoFaCode.length < 6}
                        data-testid="login-2fa-submit"
                        className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-sm tracking-wide transition-all"
                      >
                        {isLoading ? "..." : "Verifizieren & Anmelden"}
                      </Button>
                    </form>
                    <button
                      onClick={() => { setTwoFaStep(false); setTwoFaToken(""); setTwoFaCode(""); setError(""); }}
                      data-testid="login-2fa-back"
                      className="w-full text-center text-sm text-white/40 hover:text-[#00e5ff]/80 transition-colors"
                    >
                      ← Zurück zur Anmeldung
                    </button>
                  </div>
                )}

                {mode === "register" && (
                  <div className="space-y-5" data-testid="register-form-container">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-px bg-[#00e5ff]/60" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit">Registrieren</span>
                      </div>
                      <h2 className="font-outfit font-bold text-2xl text-white tracking-tight">Zugang beantragen</h2>
                      <p className="text-xs text-white/40 mt-1">Ein Admin prüft und schaltet dein Konto frei.</p>
                    </div>
                    {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2.5" data-testid="auth-error">{error}</div>}
                    <form onSubmit={handleRegister} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">Vorname *</Label>
                          <Input required value={regFirstName} onChange={(e) => setRegFirstName(e.target.value)}
                            data-testid="register-firstname-input" className="mt-1.5 bg-black border-white/10 text-white rounded-none h-10 focus:border-[#00e5ff]/60 focus:ring-0" />
                        </div>
                        <div>
                          <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">Nachname *</Label>
                          <Input required value={regLastName} onChange={(e) => setRegLastName(e.target.value)}
                            data-testid="register-lastname-input" className="mt-1.5 bg-black border-white/10 text-white rounded-none h-10 focus:border-[#00e5ff]/60 focus:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">E-Mail *</Label>
                        <Input type="email" required value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                          data-testid="register-email-input" className="mt-1.5 bg-black border-white/10 text-white rounded-none h-10 focus:border-[#00e5ff]/60 focus:ring-0" />
                      </div>
                      <div>
                        <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">Passwort * (min. 8)</Label>
                        <div className="relative mt-1.5">
                          <Input type={showPassword ? "text" : "password"} required minLength={8} value={regPassword} onChange={(e) => setRegPassword(e.target.value)}
                            data-testid="register-password-input" className="bg-black border-white/10 text-white rounded-none h-10 pr-10 focus:border-[#00e5ff]/60 focus:ring-0" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-[#00e5ff]/80 transition-colors">
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">Passwort bestätigen *</Label>
                        <Input type="password" required minLength={8} value={regConfirm} onChange={(e) => setRegConfirm(e.target.value)}
                          data-testid="register-confirm-input" className="mt-1.5 bg-black border-white/10 text-white rounded-none h-10 focus:border-[#00e5ff]/60 focus:ring-0" />
                      </div>
                      <label className="flex items-start gap-2 cursor-pointer pt-1">
                        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="accept-terms"
                          className="mt-0.5 accent-[#00e5ff]" />
                        <span className="text-xs text-white/50">Ich habe die <a href="/legal" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#00e5ff]/80">Nutzungsbedingungen</a> gelesen und stimme diesen zu.</span>
                      </label>
                      <Button type="submit" disabled={isLoading} data-testid="register-submit-button"
                        className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-sm tracking-wide transition-all">
                        {isLoading ? "..." : "Zugang beantragen"}
                      </Button>
                    </form>
                    <button onClick={() => { setMode("login"); setError(""); }} data-testid="switch-to-login"
                      className="w-full text-center text-sm text-white/40 hover:text-[#00e5ff]/80 transition-colors">
                      Bereits Mitglied? <span className="underline underline-offset-4">Anmelden</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* ===== FOOTER =============================================== */}
      {/* ============================================================ */}
      <footer className="border-t border-white/[0.06] py-8 sm:py-10 px-4 bg-black" data-testid="landing-footer">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-sm text-white/50 text-center sm:text-left">
            <span className="font-outfit font-bold text-base">
              <span className="text-[#00e5ff]">[</span><span className="text-[#00e5ff]">KI</span><span className="text-[#00e5ff]">]</span>
              <span className="text-white/80">NPUT</span>
            </span>
            <span className="hidden sm:inline text-white/20">·</span>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.24em] text-white/40 font-outfit">powered by</span>
              <img
                src="https://customer-assets.emergentagent.com/job_design-skills-hub-3/artifacts/j8c4vziw_teamwerk_Logo_RZ_simple.svg"
                alt="teamWERK Erolzheim"
                className="h-7 w-auto opacity-85"
                style={{ filter: "invert(1) brightness(1.1)" }}
                data-testid="footer-teamwerk-logo"
              />
            </div>
            <span className="hidden md:inline text-white/20">·</span>
            <span className="hidden md:inline">Bürokult GmbH</span>
          </div>
          <a href="/legal" className="text-sm text-white/50 hover:text-[#00e5ff] transition-colors">
            Impressum · Datenschutz · Nutzungsbedingungen
          </a>
        </div>
      </footer>

      {/* ============================================================ */}
      {/* ===== STYLES =============================================== */}
      {/* ============================================================ */}
      <style>{`
        /* ===== Ambient Glow Blobs ===== */
        .hero-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          will-change: transform;
        }
        .hero-blob-1 {
          top: -10%;
          left: -10%;
          width: 45%;
          height: 55%;
          background: radial-gradient(circle, rgba(0,229,255,0.12) 0%, transparent 70%);
          animation: blob-float-1 18s ease-in-out infinite;
        }
        .hero-blob-2 {
          bottom: -20%;
          right: -10%;
          width: 55%;
          height: 65%;
          background: radial-gradient(circle, rgba(0,229,255,0.09) 0%, transparent 70%);
          animation: blob-float-2 22s ease-in-out infinite;
        }
        .hero-blob-3 {
          top: 30%;
          right: 10%;
          width: 30%;
          height: 35%;
          background: radial-gradient(circle, rgba(120,0,255,0.08) 0%, transparent 70%);
          animation: blob-float-3 26s ease-in-out infinite;
        }
        @keyframes blob-float-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.08); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }
        @keyframes blob-float-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, -30px) scale(1.1); }
        }
        @keyframes blob-float-3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(25px, -40px) scale(0.92); }
        }

        /* ===== Hero Grid Background ===== */
        .hero-grid {
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
        }

        /* ===== Noise Texture ===== */
        .hero-noise {
          background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
        }

        /* ===== Hero reveal ===== */
        .hero-reveal {
          animation: hero-fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
        }
        @keyframes hero-fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ===== Scroll reveal ===== */
        .reveal-up {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-up.is-revealed {
          opacity: 1;
          transform: translateY(0);
        }

        .stat-item {
          opacity: 0;
          transform: translateY(12px);
          animation: stat-reveal 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          animation-delay: calc(var(--i, 0) * 0.08s);
        }
        @keyframes stat-reveal {
          to { opacity: 1; transform: translateY(0); }
        }

        /* ===== Auth Modal animation ===== */
        .auth-modal-overlay {
          animation: overlay-fade 0.25s ease-out;
        }
        @keyframes overlay-fade { from { opacity: 0; } to { opacity: 1; } }
        .auth-modal-card {
          animation: modal-scale 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes modal-scale {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* ===== Reduced motion ===== */
        @media (prefers-reduced-motion: reduce) {
          .hero-blob, .hero-reveal, .reveal-up, .stat-item,
          .auth-modal-overlay, .auth-modal-card {
            animation: none !important;
            transition: none !important;
          }
          .reveal-up { opacity: 1; transform: none; }
          .stat-item { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}
